import React, { useEffect, useState, useMemo } from 'react';
import { fetchAllPages } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { TablePagination } from '@/components/common/TablePagination';
import { IconButton } from '@/components/common/IconButton';
import { Search, Users, CheckCircle2, AlertTriangle, Building2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

function SummaryTile({ label, value, icon: Icon, tone = 'neutral', testid }) {
  const tones = {
    neutral: 'border-border',
    positive: 'border-emerald-200 bg-emerald-50/40 text-emerald-700',
    warn: 'border-amber-200 bg-amber-50/40 text-amber-700',
    info: 'border-sky-200 bg-sky-50/40 text-sky-700',
  };
  return (
    <div data-testid={testid} className={`rounded-lg border bg-white p-3 ${tones[tone].split(' ').slice(0,2).join(' ')}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${tones[tone].split(' ').slice(2).join(' ') || 'text-foreground'}`}>{value}</div>
    </div>
  );
}

export default function StaffListPage() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const staff = await fetchAllPages('/staff');
        setStaffList(staff || []);
      } catch (e) { toast.error('Failed to load staff'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const filtered = useMemo(() => staffList.filter(s => {
    if (complianceFilter !== 'all' && s.compliance_status !== complianceFilter) return false;
    if (search && !s.full_name?.toLowerCase().includes(search.toLowerCase()) && !s.email?.toLowerCase().includes(search.toLowerCase()) && !s.employee_id?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [staffList, complianceFilter, search]);

  // Reset to first page whenever the filtered result set changes.
  useEffect(() => { setPage(1); }, [search, complianceFilter]);
  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  // KPI band — additive only, computed client-side from current data set.
  const kpis = useMemo(() => {
    const branches = new Set();
    let compliant = 0;
    let nonCompliant = 0;
    for (const s of staffList) {
      if (s.branch_id) branches.add(s.branch_id);
      if (s.compliance_status === 'COMPLIANT') compliant += 1;
      else if (s.compliance_status === 'NON_COMPLIANT') nonCompliant += 1;
    }
    return { total: staffList.length, compliant, nonCompliant, branches: branches.size };
  }, [staffList]);

  const hasFilter = complianceFilter !== 'all' || !!search;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="staff-list-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Staff Profiles</h1>
        <p className="text-muted-foreground mt-1">Staff compliance and profile management</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="staff-summary">
        <SummaryTile label="Total staff" value={kpis.total} icon={Users} testid="staff-summary-total" />
        <SummaryTile label="Compliant" value={kpis.compliant} icon={CheckCircle2} tone="positive" testid="staff-summary-compliant" />
        <SummaryTile label="Non-compliant" value={kpis.nonCompliant} icon={AlertTriangle} tone={kpis.nonCompliant > 0 ? 'warn' : 'neutral'} testid="staff-summary-non-compliant" />
        <SummaryTile label="Branches" value={kpis.branches} icon={Building2} tone="info" testid="staff-summary-branches" />
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search staff..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="staff-search" />
          </div>
          <Select value={complianceFilter} onValueChange={setComplianceFilter}>
            <SelectTrigger className="w-[180px]" data-testid="compliance-filter"><SelectValue placeholder="Compliance" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Compliance</SelectItem>
              <SelectItem value="COMPLIANT">Compliant</SelectItem>
              <SelectItem value="NON_COMPLIANT">Non-Compliant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} staff member(s)</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} columns={5} testid="staff-loading" />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Users}
              title={hasFilter ? 'No staff match these filters' : 'No staff yet'}
              description={hasFilter ? 'Try clearing the search or compliance filter.' : 'Staff profiles will appear here once users are onboarded.'}
              testid="staff-empty"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Name</TableHead><TableHead>Employee ID</TableHead><TableHead>Compliance</TableHead><TableHead>Branch</TableHead><TableHead>Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {paged.map(s => (
                  <TableRow key={s.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/staff/${s.user_id || s.id}`)} data-testid={`staff-row-${s.id}`}>
                    <TableCell className="font-medium">{s.full_name || 'N/A'}</TableCell>
                    <TableCell className="font-mono text-xs">{s.employee_id || '-'}</TableCell>
                    <TableCell><StatusBadge status={s.compliance_status === 'COMPLIANT' ? 'APPROVED' : 'REJECTED'} /></TableCell>
                    <TableCell>{s.branch_id || '-'}</TableCell>
                    <TableCell>
                      <IconButton
                        label="View profile"
                        onClick={(e) => { e.stopPropagation(); navigate(`/staff/${s.user_id || s.id}`); }}
                        testid={`staff-edit-${s.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <TablePagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} testid="staff-list-pagination" />
        </CardContent>
      </Card>
    </div>
  );
}
